"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { useDocumentEditorStore } from "@/stores/use-document-editor-store"
import { setCloseAttemptHandler, useGlobalDialogStore } from "@/stores/use-global-dialog-store"
import { Button, Skeleton } from "@ui/shad"
import { Download, Loader2, Redo2, RefreshCw, Undo2, Upload, X } from "lucide-react"
import { toast } from "sonner"

import { ApiRequestError } from "@/hooks/api/base"
import { useDeliverable, usePublishDeliverable, useSaveDeliverableDraft } from "@/hooks/api/deliverables"
import { useBookId } from "@/hooks/use-book-id"
import { ArtifactSaveNotice } from "@/components/artifact-editor/save-notice"
import { ConfirmDialog } from "@/components/dialogs"
import { useDialogEntryId } from "@/components/dialogs/dialog-entry-context"
import {
  DocumentEditFrame,
  type DocumentEditFrameHandle,
  type DocumentEditHistory,
} from "@/components/document-editor/document-edit-frame"

const AUTOSAVE_DELAY_MS = 1_200
const SAVED_NOTICE_MS = 2_000
const EMPTY_HISTORY: DocumentEditHistory = { canUndo: false, canRedo: false }

export function DocumentPreviewFrame({
  deliverableId,
  actionLinkId,
}: {
  deliverableId: string
  actionLinkId?: string
}) {
  const { data: deliverable } = useDeliverable(deliverableId)
  const publish = usePublishDeliverable(deliverableId, actionLinkId)
  const saveDraft = useSaveDeliverableDraft(deliverableId)
  const { closeDialog, popDialog } = useGlobalDialogStore()
  const entryId = useDialogEntryId()
  const bookId = useBookId()
  const chatBusy = useDocumentEditorStore((state) => state.chatBusy)
  const dirty = useDocumentEditorStore((state) => state.dirty)
  const setDirty = useDocumentEditorStore((state) => state.setDirty)
  const registerSave = useDocumentEditorStore((state) => state.registerSave)
  const editFrameRef = useRef<DocumentEditFrameHandle>(null)
  const autosaveTimer = useRef<number | null>(null)
  const editVersion = useRef(0)
  const saveQueue = useRef<Promise<boolean>>(Promise.resolve(true))
  const closing = useRef(false)
  const [reloadToken, setReloadToken] = useState(0)
  const [saving, setSaving] = useState(false)
  const [justSaved, setJustSaved] = useState(false)
  const [saveConflict, setSaveConflict] = useState(false)
  const [history, setHistory] = useState(EMPTY_HISTORY)
  const [confirmPublishOpen, setConfirmPublishOpen] = useState(false)
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false)
  const [missingDataBannerDismissed, setMissingDataBannerDismissed] = useState(false)

  const cancelScheduledSave = () => {
    if (autosaveTimer.current !== null) window.clearTimeout(autosaveTimer.current)
    autosaveTimer.current = null
  }

  const runSave = useCallback(async (): Promise<boolean> => {
    cancelScheduledSave()
    const versionAtStart = editVersion.current
    setSaving(true)
    const attempt = (async () => {
      try {
        const draft = await editFrameRef.current!.requestDraft()
        const saved = await saveDraft.mutateAsync(draft)
        editFrameRef.current?.adoptRevision(saved.editor_revision)
        setSaveConflict(false)
        if (editVersion.current === versionAtStart) {
          setDirty(false)
          setJustSaved(true)
        }
        return true
      } catch (error) {
        if (error instanceof ApiRequestError && error.status === 409) setSaveConflict(true)
        return false
      } finally {
        setSaving(false)
      }
    })()
    return attempt
  }, [saveDraft, setDirty])

  // Every caller (autosave, blur, chat, close) joins one queue, so saves never
  // run concurrently and each waiter sees the outcome of the newest edits.
  const saveEdits = useCallback((): Promise<boolean> => {
    const run = saveQueue.current.then(async () => {
      while (useDocumentEditorStore.getState().dirty && editFrameRef.current) {
        if (!(await runSave())) return false
      }
      return true
    })
    saveQueue.current = run.catch(() => false)
    return run
  }, [runSave])

  const scheduleSave = useCallback(() => {
    cancelScheduledSave()
    autosaveTimer.current = window.setTimeout(() => void saveEdits(), AUTOSAVE_DELAY_MS)
  }, [saveEdits])

  const handleDirtyChange = useCallback(
    (nextDirty: boolean) => {
      if (!nextDirty) return
      editVersion.current += 1
      setJustSaved(false)
      setDirty(true)
      scheduleSave()
    },
    [scheduleSave, setDirty]
  )

  const reloadDocument = () => {
    cancelScheduledSave()
    setDirty(false)
    setSaveConflict(false)
    setHistory(EMPTY_HISTORY)
    setReloadToken((token) => token + 1)
  }

  useEffect(() => {
    if (!justSaved) return
    const timeout = window.setTimeout(() => setJustSaved(false), SAVED_NOTICE_MS)
    return () => window.clearTimeout(timeout)
  }, [justSaved])

  useEffect(() => cancelScheduledSave, [])

  useEffect(() => {
    registerSave(saveEdits)
    return () => registerSave(null)
  }, [registerSave, saveEdits])

  const attemptClose = useCallback(() => {
    if (closing.current) return
    closing.current = true
    void saveEdits()
      .then((saved) => {
        if (saved) popDialog()
        else setConfirmCloseOpen(true)
      })
      .finally(() => {
        closing.current = false
      })
  }, [popDialog, saveEdits])

  useEffect(() => {
    if (!entryId) return
    setCloseAttemptHandler(attemptClose, entryId)

    return () => setCloseAttemptHandler(null, entryId)
  }, [attemptClose, entryId])

  const handlePublish = () => {
    setConfirmPublishOpen(false)
    publish.mutate(undefined, {
      onSuccess: () => {
        toast.success("PDF published")
        closeDialog()
      },
    })
  }

  const publishBlocked =
    deliverable?.status === "building" || deliverable?.status === "failed"
      ? "Your document is not ready to publish"
      : dirty || saving
        ? "Your latest edits are still saving"
        : undefined

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-md border">
      <div className="text-muted-foreground bg-muted flex h-9 shrink-0 items-center justify-between gap-2 border-b py-1 pr-1.5 pl-3 text-xs font-medium">
        <span className="flex items-center gap-2">
          Document
          {chatBusy ? (
            <span className="bg-blue-background text-blue-accent flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium">
              <Loader2 className="size-3 animate-spin" />
              Chat is editing
            </span>
          ) : saveConflict ? (
            <span className="bg-red-background text-red-accent flex items-center gap-1.5 rounded-full py-0.5 pr-1 pl-2 text-[11px] font-medium">
              This document changed elsewhere
              <Button variant="ghost" size="xs" onClick={reloadDocument} className="h-5 px-1.5 text-[11px]">
                <RefreshCw />
                Reload and drop my edits
              </Button>
            </span>
          ) : saving ? (
            <ArtifactSaveNotice state="saving" className="text-[11px]" />
          ) : dirty ? (
            <span className="text-[11px]">Unsaved edits</span>
          ) : justSaved ? (
            <ArtifactSaveNotice state="saved" className="text-[11px]" />
          ) : (
            deliverable?.has_unpublished_changes && (
              <span className="bg-yellow-background text-yellow-accent rounded-full px-2 py-0.5 text-[11px] font-medium">
                Unpublished changes
              </span>
            )
          )}
        </span>
        <div className="flex items-center gap-1.5">
          {deliverable?.download_url && (
            <Button variant="outline" size="sm" asChild>
              <a href={deliverable.download_url} target="_blank" rel="noopener noreferrer">
                <Download /> Download PDF
              </a>
            </Button>
          )}
          <Button
            variant="ghost"
            size="iconSm"
            onClick={() => editFrameRef.current?.undo()}
            disabled={!history.canUndo || chatBusy}
            tooltip="Undo"
            aria-label="Undo"
          >
            <Undo2 />
          </Button>
          <Button
            variant="ghost"
            size="iconSm"
            onClick={() => editFrameRef.current?.redo()}
            disabled={!history.canRedo || chatBusy}
            tooltip="Redo"
            aria-label="Redo"
          >
            <Redo2 />
          </Button>
          <Button
            size="sm"
            onClick={() => setConfirmPublishOpen(true)}
            disabled={!deliverable?.has_unpublished_changes || publish.isPending || !!publishBlocked}
            tooltip={publishBlocked}
          >
            {publish.isPending ? <Loader2 className="animate-spin" /> : <Upload />}
            {publish.isPending ? "Publishing…" : "Publish"}
          </Button>
        </div>
      </div>
      {deliverable?.missing_data_form_id && !missingDataBannerDismissed && (
        <div className="bg-yellow-background border-yellow-border text-yellow-accent flex shrink-0 items-center justify-between gap-3 border-b px-3 py-2 text-xs">
          <p>More information is needed to complete this plan.</p>
          <div className="flex items-center gap-1">
            <Button size="xs" variant="outline" asChild>
              <a
                href={`/${bookId}/intake-forms/${deliverable.missing_data_form_id}/edit`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Review form
              </a>
            </Button>
            <Button
              variant="ghost"
              size="iconXs"
              onClick={() => setMissingDataBannerDismissed(true)}
              aria-label="Dismiss"
            >
              <X className="text-foreground" />
            </Button>
          </div>
        </div>
      )}
      <div className="flex min-h-0 flex-1 flex-col">
        {deliverable?.status === "building" ? (
          <div role="status" className="text-muted-foreground flex flex-1 items-center justify-center gap-2">
            <Loader2 className="size-4 animate-spin" /> Preparing document…
          </div>
        ) : deliverable?.status === "failed" ? (
          <p role="alert" className="text-red-accent p-4 text-sm">
            {deliverable.error_message || "Unable to prepare this document. Close it and try again."}
          </p>
        ) : deliverable ? (
          <DocumentEditFrame
            key={reloadToken}
            ref={editFrameRef}
            deliverableId={deliverableId}
            editable={!chatBusy}
            onDirtyChange={handleDirtyChange}
            onHistoryChange={setHistory}
            onBlur={saveEdits}
            className="min-h-0 w-full flex-1 rounded-none border-0 bg-white"
          />
        ) : (
          <Skeleton className="min-h-0 w-full flex-1 rounded-none" />
        )}
      </div>
      <ConfirmDialog
        open={confirmPublishOpen}
        onOpenChange={setConfirmPublishOpen}
        title={"Replace the PDF?"}
        description={"Publishing converts your edits to a new PDF. The download link in chat will serve the new file."}
        dismiss={publish.isPending ? "none" : "explicit"}
        cancel={{ onClick: () => setConfirmPublishOpen(false), disabled: publish.isPending }}
        action={{ label: "Publish", onClick: handlePublish, pending: publish.isPending }}
      />
      <ConfirmDialog
        open={confirmCloseOpen}
        onOpenChange={setConfirmCloseOpen}
        title={"Close without saving?"}
        description={"Your latest changes could not be saved. Closing now will discard them."}
        dismiss={"explicit"}
        cancel={{ onClick: () => setConfirmCloseOpen(false) }}
        action={{
          label: "Discard and close",
          destructive: true,
          onClick: () => {
            setConfirmCloseOpen(false)
            cancelScheduledSave()
            setDirty(false)
            popDialog()
          },
        }}
      />
    </div>
  )
}
